import { Button } from "./button";
import { Card, CardContent } from "./card";
import { CheckCircle2, ArrowRight, ExternalLink, Copy, Check } from "lucide-react";
import { useState } from "react";

interface ConversionSuccessProps {
  open: boolean;
  onClose: () => void;
  onViewBalance: () => void;
  fromCurrency: "BRL" | "USDC";
  toCurrency: "BRL" | "USDC";
  fromAmount: number;
  toAmount: number;
  exchangeRate: number;
  newBrldBalance: number;
  newUsdcBalance: number;
  burnTxHash?: string;
  mintTxHash?: string;
  explorerBaseUrl?: string;
}

export function ConversionSuccess({
  open,
  onClose,
  onViewBalance,
  fromCurrency,
  toCurrency,
  fromAmount,
  toAmount,
  exchangeRate,
  newBrldBalance,
  newUsdcBalance,
  burnTxHash,
  mintTxHash,
  explorerBaseUrl = "https://xdcscan.io",
}: ConversionSuccessProps) {
  const [copiedTx, setCopiedTx] = useState<string | null>(null);

  if (!open) return null;

  const formatBrl = (cents: number) => {
    return (cents / 100).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  const formatUsdc = (cents: number) => {
    return (cents / 100).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
  };

  const formatAmount = (amount: number, currency: "BRL" | "USDC") => {
    return currency === "BRL" ? formatBrl(amount) : formatUsdc(amount);
  };

  const handleCopyTx = async (hash: string) => {
    await navigator.clipboard.writeText(hash);
    setCopiedTx(hash);
    setTimeout(() => setCopiedTx(null), 2000);
  };

  const truncateHash = (hash: string) => {
    return `${hash.slice(0, 10)}...${hash.slice(-8)}`;
  };

  const txHash = fromCurrency === "BRL" ? burnTxHash : mintTxHash;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
      <Card className="w-full max-w-md mx-4 overflow-hidden">
        <CardContent className="pt-8 pb-6 space-y-6">
          {/* Header de sucesso */}
          <div className="text-center space-y-3">
            {/* Animação de sucesso */}
            <div className="relative mx-auto w-20 h-20">
              <div className="absolute inset-0 rounded-full bg-green-500/20 animate-pulse" />
              <div className="relative w-20 h-20 rounded-full bg-green-500 flex items-center justify-center">
                <CheckCircle2 className="w-10 h-10 text-white" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-green-600">Conversão Realizada!</h2>
            <p className="text-muted-foreground">
              Sua conversão foi processada com sucesso
            </p>
          </div>

          {/* Resumo da conversão */}
          <div className="bg-gradient-to-r from-primary/5 to-primary/10 rounded-xl p-4 space-y-4">
            {/* Valores convertidos */}
            <div className="flex items-center justify-center gap-3">
              <div className="text-center">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">De</p>
                <p className="text-lg font-bold">{formatAmount(fromAmount, fromCurrency)}</p>
                <p className="text-xs text-muted-foreground">
                  {fromCurrency === "BRL" ? "BRLD" : "USDC"}
                </p>
              </div>
              <ArrowRight className="w-5 h-5 text-primary" />
              <div className="text-center">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Para</p>
                <p className="text-lg font-bold text-primary">{formatAmount(toAmount, toCurrency)}</p>
                <p className="text-xs text-muted-foreground">
                  {toCurrency === "BRL" ? "BRLD" : "USDC"}
                </p>
              </div>
            </div>

            {/* Taxa */}
            <div className="text-center text-sm text-muted-foreground border-t border-primary/10 pt-3">
              Taxa: 1 USDC = R$ {exchangeRate.toFixed(4)}
            </div>
          </div>

          {/* Novos saldos */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-muted/50 rounded-lg p-3 text-center">
              <p className="text-xs text-muted-foreground">Novo Saldo BRLD</p>
              <p className="font-semibold text-sm">{formatBrl(newBrldBalance)}</p>
            </div>
            <div className="bg-muted/50 rounded-lg p-3 text-center">
              <p className="text-xs text-muted-foreground">Novo Saldo USDC</p>
              <p className="font-semibold text-sm">{formatUsdc(newUsdcBalance)}</p>
            </div>
          </div>

          {/* Hash da transação blockchain */}
          {txHash && (
            <div className="bg-muted/30 rounded-lg p-3 space-y-2">
              <p className="text-xs text-muted-foreground">
                {fromCurrency === "BRL" ? "Transação de Burn (Blockchain)" : "Transação de Mint (Blockchain)"}
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-muted px-2 py-1 rounded font-mono">
                  {truncateHash(txHash)}
                </code>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => handleCopyTx(txHash)}
                >
                  {copiedTx === txHash ? (
                    <Check className="w-3.5 h-3.5 text-green-500" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  asChild
                >
                  <a
                    href={`${explorerBaseUrl}/tx/${txHash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </Button>
              </div>
            </div>
          )}

          {/* Botões */}
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={onClose}>
              Fechar
            </Button>
            <Button className="flex-1" onClick={onViewBalance}>
              Ver Saldo {toCurrency === "USDC" ? "USDC" : "BRLD"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
